'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getClubsByStaff } from '@/api/clubs/clubApi';
import { getOrganizedMatches, getOrganizedMatchFee } from '@/api/match/organizedMatchApi';
import { TurnosGrid, TurnosLegend } from '@/components/organized-matches/TurnosGrid';
import {
  OrganizedMatchSheet,
  type SheetState,
} from '@/components/organized-matches/OrganizedMatchSheet';
import {
  buildWeekGrid,
  courtCountBySport,
  DEFAULT_TURN_DURATION,
  turnEnd,
  weekStartOf,
  type TurnDuration,
  type WeekCell,
} from '@/components/organized-matches/turnos-model';
import { useGridTimeRows } from '@/components/tournament/grid/useGridTimeRows';
import { useAuthStore } from '@/store/authStore';
import { handleGlobalError } from '@/utils/error-handler';
import { formatDateForUser } from '@/utils/formatters';
import { addDays } from '@/utils/localDateOnly';
import { logError } from '@/utils/logger';
import type { Club } from '@/types/club.model';
import type { OrganizedMatch } from '@/types/organized-match.model';
import type { Sport } from '@/types/sports.model';

const DURATIONS: TurnDuration[] = [60, 90, 120];

/** Club-local date ("yyyy-MM-dd") and time ("HH:mm") now; falls back to the browser timezone. */
function nowIn(timezone?: string): { date: string; now: string } {
  const read = (tz?: string) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date());
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const date = `${get('year')}-${get('month')}-${get('day')}`;
    return { date, now: `${date}T${get('hour')}:${get('minute')}` };
  };
  try {
    return read(timezone);
  } catch {
    return read();
  }
}

const todayIn = (timezone?: string): string => nowIn(timezone).date;

export default function TurnosPage() {
  const user = useAuthStore((s) => s.user);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [clubId, setClubId] = useState('');
  const [weekStart, setWeekStart] = useState(() => weekStartOf(todayIn()));
  const [duration, setDuration] = useState<TurnDuration>(DEFAULT_TURN_DURATION);
  const [matches, setMatches] = useState<OrganizedMatch[]>([]);
  const [fee, setFee] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState<SheetState | null>(null);
  // Rows added or taken out by hand: another club starts clean.
  const { extraTimes, hiddenTimes, addTime, removeTime, showHiddenTimes, resetTimes } =
    useGridTimeRows();

  const club = clubs.find((c) => c.id === clubId);
  const { date: today, now } = nowIn(club?.address?.timezone);

  // Picking a club also starts on its local week, so the change costs a single request.
  const selectClub = useCallback(
    (id: string, list: Club[]) => {
      setClubId(id);
      resetTimes();
      setWeekStart(weekStartOf(todayIn(list.find((c) => c.id === id)?.address?.timezone)));
    },
    [resetTimes],
  );

  // Clubs and the organized-match fee are requested once, on mount.
  useEffect(() => {
    getClubsByStaff()
      .then((data) => {
        setClubs(data);
        if (data.length === 1) selectClub(data[0].id, data);
      })
      .catch((err) =>
        handleGlobalError(
          err,
          'No se pudieron cargar los clubes',
          'Probá de nuevo en unos segundos.',
        ),
      )
      .finally(() => setLoading(false));
    getOrganizedMatchFee()
      .then(setFee)
      .catch((err) => logError('TurnosPage.loadFee', err));
  }, [selectClub]);

  // Only the latest request may write state: a slow response for another week or club must not win.
  const requestId = useRef(0);

  const loadMatches = useCallback(async () => {
    const id = ++requestId.current;
    if (!clubId) {
      setMatches([]);
      return;
    }
    try {
      const data = await getOrganizedMatches(clubId, weekStart, addDays(weekStart, 6));
      if (id === requestId.current) setMatches(data);
    } catch (err) {
      if (id !== requestId.current) return;
      handleGlobalError(
        err,
        'No se pudieron cargar los turnos',
        'Probá de nuevo en unos segundos.',
      );
    }
  }, [clubId, weekStart]);

  useEffect(() => {
    setSheet(null);
    // Never show another club's or week's matches on this grid while loading.
    setMatches([]);
    void loadMatches();
  }, [loadMatches]);

  const grid = useMemo(
    () =>
      buildWeekGrid(
        club?.availability ?? [],
        weekStart,
        duration,
        matches,
        courtCountBySport(club?.courts ?? []),
        { now, extraTimes, hiddenTimes },
      ),
    // `now` is minute-precise: the grid refreshes with the next render after the minute changes.
    [club, weekStart, duration, matches, now, extraTimes, hiddenTimes],
  );

  const sports = useMemo<Sport[]>(() => {
    const byUid = new Map<string, Sport>();
    (club?.courts ?? []).forEach((court) => {
      if (court.sport?.uid) byUid.set(court.sport.uid, court.sport);
    });
    return [...byUid.values()];
  }, [club]);

  const openCreate = (cell: WeekCell) =>
    setSheet({
      mode: 'create',
      clubId,
      startDate: `${cell.date}T${cell.time}:00`,
      endDate: turnEnd(cell.date, cell.time, duration),
      freeCourts: cell.freeCourts,
    });

  const weekEnd = addDays(weekStart, 6);
  const weekLabel = `${formatDateForUser(weekStart, 'dd/MM')} al ${formatDateForUser(weekEnd, 'dd/MM')}`;

  return (
    <div className="flex flex-col gap-4 p-4">
      <h1 className="text-2xl font-bold">Turnos</h1>

      <div className="flex flex-wrap items-end gap-4">
        <div className="w-56">
          <Label>Club</Label>
          <Select value={clubId} onValueChange={(id) => selectClub(id, clubs)}>
            <SelectTrigger aria-label="Club">
              <SelectValue placeholder="Elegí un club" />
            </SelectTrigger>
            <SelectContent>
              {clubs.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Semana anterior"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[110px] text-center text-sm font-medium">{weekLabel}</span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Semana siguiente"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="w-36">
          <Label>Duración</Label>
          <Select
            value={String(duration)}
            onValueChange={(v) => setDuration(Number(v) as TurnDuration)}
          >
            <SelectTrigger aria-label="Duración">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DURATIONS.map((d) => (
                <SelectItem key={d} value={String(d)}>
                  {d} minutos
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando...</p>
      ) : !club ? (
        <p className="text-sm text-muted-foreground">
          {clubs.length === 0 ? 'No tenés clubes habilitados.' : 'Elegí un club para ver sus turnos.'}
        </p>
      ) : (
        <>
          <TurnosGrid
            grid={grid}
            today={today}
            onCreate={openCreate}
            onOpen={(match) => setSheet({ mode: 'view', match })}
            onAddTime={addTime}
            onRemoveTime={removeTime}
            onShowHiddenTimes={showHiddenTimes}
          />
          <TurnosLegend />
        </>
      )}

      <OrganizedMatchSheet
        state={sheet}
        sports={sports}
        fee={fee}
        now={now}
        currentUserEmail={user?.email}
        onModeChange={setSheet}
        onClose={() => setSheet(null)}
        onChanged={() => void loadMatches()}
      />
    </div>
  );
}
